import React, { useEffect, useState } from 'react';

import { Button, Input, Modal, dialogService, toastService } from '@thefarmersfront/kitchen';

import { FormField } from '~/components/common';

import { useGetOwnerManagerExistsOwnerLoginId, usePostOwnerManager } from '~/hooks/vender';
import { useLoadingStore } from '~/store';
import { validateEmailFormat, validateIdFormat, validateTelFormat, validateVenderManagerName } from '~/utils';

import { DEFAULT_INPUT_STATUS, TOAST_MESSAGE, VENDERS } from '~/constants';

import { SuccessInfoMessage } from '../../SuccessInfoMessage';

import type { TypeErrorResponseData, TypeInputState } from '~/types/common.types';

import styles from './VenderOwnerManagerAddModal.module.scss';

type TypeVenderOwnerManagerAddModalProps = {
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void;
};

const DEFAULT_MANAGER_ADD_FORM = {
  id: '',
  tempPassword: '',
  name: '',
  position: '',
  tel: '',
  email: '',
};

/**
 * [화주사정보관리 > 화주사정보] 담당자 정보 추가 모달 컴포넌트
 */
const VenderOwnerManagerAddModal = ({ open, onCancel, onConfirm }: TypeVenderOwnerManagerAddModalProps) => {
  const { getOwnerManagerExistsOwnerLoginId } = useGetOwnerManagerExistsOwnerLoginId();
  const { postOwnerManager } = usePostOwnerManager();

  const { setIsLoading } = useLoadingStore();

  const [managerAddForm, setManagerAddForm] = useState(DEFAULT_MANAGER_ADD_FORM);

  const [isLoginIdDuplicated, setIsLoginIdDuplicated] = useState<boolean | null>(null);

  const [idStatus, setIdStatus] = useState<TypeInputState>(DEFAULT_INPUT_STATUS);
  const [tempPasswordStatus, setTempPasswordStatus] = useState<TypeInputState>(DEFAULT_INPUT_STATUS);
  const [nameStatus, setNameStatus] = useState<TypeInputState>(DEFAULT_INPUT_STATUS);
  const [telStatus, setTelStatus] = useState<TypeInputState>(DEFAULT_INPUT_STATUS);
  const [emailStatus, setEmailStatus] = useState<TypeInputState>(DEFAULT_INPUT_STATUS);

  const fetchOwnerManagerExist = async (ownerLoginId: string) => {
    try {
      setIsLoading(true);

      const isDuplicated = await getOwnerManagerExistsOwnerLoginId({
        ownerLoginId: ownerLoginId,
      });

      if (isDuplicated) {
        setIdStatus({ status: 'error', message: '이미 사용중인 아이디입니다.' });
      } else {
        setIdStatus({ status: 'default' });
      }
      setIsLoginIdDuplicated(isDuplicated);
    } catch (error) {
      toastService.addToast({
        type: 'error',
        message: (error as TypeErrorResponseData['error'])?.message || TOAST_MESSAGE.DEFAULT_ERROR,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchOwnerManager = async () => {
    try {
      setIsLoading(true);

      const param = {
        ownerLoginId: managerAddForm.id.trim(),
        ownerLoginPwd: managerAddForm.tempPassword.trim(),
        ownerManagerName: managerAddForm.name,
        ownerManagerPosition: managerAddForm.position.trim(),
        ownerManagerTel: managerAddForm.tel.trim(),
        ownerManagerEmail: managerAddForm.email.trim(),
      };

      await postOwnerManager(param);

      toastService.addToast({
        message: '성공적으로 저장되었습니다.',
      });

      onConfirm();
    } catch (error) {
      if ((error as TypeErrorResponseData['error'])?.code === VENDERS.DUPLICATED_MANAGER_NAME_AND_EMAIL) {
        dialogService.addDialog({
          children: (error as TypeErrorResponseData['error'])?.message,
        });
      } else {
        toastService.addToast({
          type: 'error',
          message: (error as TypeErrorResponseData['error'])?.message || TOAST_MESSAGE.DEFAULT_ERROR,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const resetAllInputStatus = () => {
    setManagerAddForm(DEFAULT_MANAGER_ADD_FORM);

    setIsLoginIdDuplicated(null);

    setIdStatus(DEFAULT_INPUT_STATUS);
    setTempPasswordStatus(DEFAULT_INPUT_STATUS);
    setNameStatus(DEFAULT_INPUT_STATUS);
    setTelStatus(DEFAULT_INPUT_STATUS);
    setEmailStatus(DEFAULT_INPUT_STATUS);
  };

  useEffect(() => {
    if (!open) {
      resetAllInputStatus();
    }
  }, [open]);

  const handleFocusId = () => setIdStatus(DEFAULT_INPUT_STATUS);

  const handleFocusTempPassword = () => setTempPasswordStatus(DEFAULT_INPUT_STATUS);

  const handleFocusName = () => setNameStatus(DEFAULT_INPUT_STATUS);

  const handleFocusTel = () => setTelStatus(DEFAULT_INPUT_STATUS);

  const handleFocusEmail = () => setEmailStatus(DEFAULT_INPUT_STATUS);

  const handleChangeId = (e: React.ChangeEvent<HTMLInputElement>) => {
    setManagerAddForm({ ...managerAddForm, id: e.target.value });
    setIsLoginIdDuplicated(null);
  };

  const handleChangeTempPassword = (e: React.ChangeEvent<HTMLInputElement>) =>
    setManagerAddForm({ ...managerAddForm, tempPassword: e.target.value });

  const handleChangeName = (e: React.ChangeEvent<HTMLInputElement>) => {
    const valueWithoutSpaces = e.target.value.replace(/\s+/g, '');
    setManagerAddForm({ ...managerAddForm, name: valueWithoutSpaces });
  };

  const handleChangePosition = (e: React.ChangeEvent<HTMLInputElement>) =>
    setManagerAddForm({ ...managerAddForm, position: e.target.value });

  const handleChangeTel = (e: React.ChangeEvent<HTMLInputElement>) =>
    setManagerAddForm({ ...managerAddForm, tel: e.target.value });

  const handleChangeEmail = (e: React.ChangeEvent<HTMLInputElement>) =>
    setManagerAddForm({ ...managerAddForm, email: e.target.value });

  const handleClickIdDuplicateCheck = () => {
    const trimmedId = managerAddForm.id.trim();

    if (trimmedId === '') {
      setIdStatus({ status: 'error', message: '필수 입력입니다.' });
      return;
    } else if (!validateIdFormat(trimmedId)) {
      setIdStatus({ status: 'error', message: '올바른 형식으로 입력해주세요.' });
      return;
    }

    fetchOwnerManagerExist(trimmedId);
  };

  const checkFormValidation = (): boolean => {
    let isValidForm = true;

    if (managerAddForm.id.trim() === '') {
      setIdStatus({ status: 'error', message: '필수 입력입니다.' });
      isValidForm = false;
    } else if (isLoginIdDuplicated === null) {
      setIdStatus({ status: 'error', message: '아이디를 [중복 확인]해주세요.' });
      isValidForm = false;
    } else if (isLoginIdDuplicated === true) {
      setIdStatus({ status: 'error', message: '이미 사용중인 아이디입니다.' });
      isValidForm = false;
    }

    if (managerAddForm.tempPassword.trim() === '') {
      setTempPasswordStatus({ status: 'error', message: '필수 입력입니다.' });
      isValidForm = false;
    }

    if (managerAddForm.name.trim() === '') {
      setNameStatus({ status: 'error', message: '필수 입력입니다.' });
      isValidForm = false;
    } else if (!validateVenderManagerName(managerAddForm.name.trim())) {
      setNameStatus({ status: 'error', message: '올바른 형식으로 입력해주세요.' });
      isValidForm = false;
    }

    if (managerAddForm.tel.trim() === '') {
      setTelStatus({ status: 'error', message: '필수 입력입니다.' });
      isValidForm = false;
    } else if (!validateTelFormat(managerAddForm.tel.trim())) {
      setTelStatus({ status: 'error', message: '올바른 형식으로 입력해주세요.' });
      isValidForm = false;
    }

    if (managerAddForm.email.trim() === '') {
      setEmailStatus({ status: 'error', message: '필수 입력입니다.' });
      isValidForm = false;
    } else if (!validateEmailFormat(managerAddForm.email.trim())) {
      setEmailStatus({ status: 'error', message: '올바른 형식으로 입력해주세요.' });
      isValidForm = false;
    }

    return isValidForm;
  };

  const handleConfirm = () => {
    const isValidForm = checkFormValidation();

    if (isValidForm) {
      fetchOwnerManager();
    }
  };

  return (
    <Modal
      open={open}
      title="담당자 추가"
      confirmText="추가"
      confirmButtonType="submit"
      onCancel={onCancel}
      onConfirm={handleConfirm}
    >
      <FormField
        label="아이디"
        required
        contents={
          <>
            <div className={styles['login-id-input']}>
              <Input
                type="text"
                size="small"
                placeholder="아이디 입력"
                autoComplete="new-id"
                status={idStatus.status}
                validationMessage={idStatus.message}
                value={managerAddForm.id}
                onFocus={handleFocusId}
                onChange={handleChangeId}
              />
              {idStatus.status === 'default' && isLoginIdDuplicated === false && (
                <SuccessInfoMessage message="사용 가능한 아이디입니다." />
              )}
            </div>
            <Button color="secondary" size="small" onClick={handleClickIdDuplicateCheck}>
              중복 확인
            </Button>
          </>
        }
        labelClassName={styles['label']}
        contentsClassName={styles['input-button-wrapper']}
      />
      <FormField
        label="임시 비밀번호"
        required
        contents={
          <Input
            type="password"
            size="small"
            placeholder="입력"
            autoComplete="new-password"
            status={tempPasswordStatus.status}
            validationMessage={tempPasswordStatus.message}
            value={managerAddForm.tempPassword}
            onFocus={handleFocusTempPassword}
            onChange={handleChangeTempPassword}
          />
        }
        labelClassName={styles['label']}
      />
      <FormField
        label="담당자명"
        required
        contents={
          <Input
            type="text"
            size="small"
            placeholder="입력"
            status={nameStatus.status}
            validationMessage={nameStatus.message}
            value={managerAddForm.name}
            onFocus={handleFocusName}
            onChange={handleChangeName}
          />
        }
        labelClassName={styles['label']}
      />
      <FormField
        label="직위"
        contents={
          <Input
            type="text"
            size="small"
            placeholder="입력"
            value={managerAddForm.position}
            onChange={handleChangePosition}
          />
        }
        labelClassName={styles['label']}
      />
      <FormField
        label="휴대폰번호"
        required
        contents={
          <Input
            type="text"
            size="small"
            placeholder="- 포함 입력"
            status={telStatus.status}
            validationMessage={telStatus.message}
            value={managerAddForm.tel}
            onFocus={handleFocusTel}
            onChange={handleChangeTel}
          />
        }
        labelClassName={styles['label']}
      />
      <FormField
        label="이메일"
        required
        contents={
          <Input
            type="text"
            size="small"
            placeholder="example@kls.com"
            status={emailStatus.status}
            validationMessage={emailStatus.message}
            value={managerAddForm.email}
            onFocus={handleFocusEmail}
            onChange={handleChangeEmail}
          />
        }
        labelClassName={styles['label']}
      />
    </Modal>
  );
};

export default VenderOwnerManagerAddModal;
